import { useEffect, useState } from 'react';
import { theme } from '@frontend/theme';
import { Avatar, Text } from '@frontend/design-system';
import { LightboxControls } from './lightbox-controls';
import { MediaLightbox } from './media-lightbox';

export type VideoPreviewProps = {
  src?: string;
  senderName?: string;
  timestamp?: string | Date;
  show: boolean;
  onClose: () => void;
  onDownload?: () => void;
};

export const VideoPreview = ({ src, senderName, timestamp, show, onClose, onDownload }: VideoPreviewProps) => {
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    if (show) {
      setRotation(0);
      setZoom(1);
    }
  }, [show, src]);

  if (!src) return null;

  const handleOpenInNewWindow = () => {
    window.open(src, '_blank', 'noopener,noreferrer');
  };

  return (
    <MediaLightbox
      show={show}
      onClose={onClose}
      header={
        <div css={{ display: 'flex', alignItems: 'center' }}>
          <Avatar name={senderName} size='small' />
          <div css={{ display: 'flex', flexDirection: 'column', marginLeft: theme.spacing(1) }}>
            <Text color='white' size='small'>
              {senderName ?? 'Video'}
            </Text>
            {timestamp && (
              <Text color='white' size='small'>
                {timestamp.toString()}
              </Text>
            )}
          </div>
        </div>
      }
      footer={
        <LightboxControls
          onRotate={() => setRotation((r) => (r + 90) % 360)}
          zoom={{ value: zoom, onChange: setZoom, min: 1, max: 3, step: 0.25 }}
          onDownload={onDownload}
          onOpenInNewWindow={handleOpenInNewWindow}
        />
      }
    >
      <video
        src={src}
        controls
        autoPlay
        playsInline
        // Click on the video itself shouldn't close the lightbox.
        onClick={(e) => e.stopPropagation()}
        css={{
          maxWidth: '90vw',
          maxHeight: '85vh',
          width: 'auto',
          height: 'auto',
          display: 'block',
          borderRadius: theme.borderRadius.medium,
          background: theme.colors.neutral90,
          transform: `rotate(${rotation}deg) scale(${zoom})`,
          transformOrigin: 'center center',
          transition: 'transform 0.2s ease',
        }}
      />
    </MediaLightbox>
  );
};
